export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface ClientLogContext {
  correlationId?: string;
  op?: string;
  component?: string;
  userId?: string;
  route?: string;
  duration_ms?: number;
  success?: boolean;
  err_code?: string;
  err_message?: string;
  sample?: boolean;
  [key: string]: any; // Allow additional context fields
}

/**
 * PII Redaction Patterns
 */
const PII_PATTERNS = {
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  phone: /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
  creditCard: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  apiKey: /(sk|pk)_[a-zA-Z0-9_]{20,}/g,
  jwt: /eyJ[a-zA-Z0-9_-]+\.eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g
};

/**
 * Redact PII from any value
 */
export function redactPII(value: any): any {
  if (typeof value === 'string') {
    let redacted = value;
    redacted = redacted.replace(PII_PATTERNS.email, '[EMAIL_REDACTED]');
    redacted = redacted.replace(PII_PATTERNS.phone, '[PHONE_REDACTED]');
    redacted = redacted.replace(PII_PATTERNS.creditCard, '[CARD_REDACTED]');
    redacted = redacted.replace(PII_PATTERNS.ssn, '[SSN_REDACTED]');
    redacted = redacted.replace(PII_PATTERNS.apiKey, '[API_KEY_REDACTED]');
    redacted = redacted.replace(PII_PATTERNS.jwt, '[JWT_REDACTED]');
    return redacted;
  }

  if (Array.isArray(value)) {
    return value.map(redactPII);
  }

  if (value && typeof value === 'object') {
    const redacted: any = {};
    for (const [key, val] of Object.entries(value)) {
      // Redact sensitive field names (case-insensitive check)
      const lowerKey = key.toLowerCase();
      if (['password', 'token', 'secret', 'apikey', 'api_key', 'authorization'].includes(lowerKey)) {
        redacted[key] = '[REDACTED]';
      } else {
        redacted[key] = redactPII(val);
      }
    }
    return redacted;
  }

  return value;
}

interface ClientLogEntry {
  ts: string;
  level: LogLevel;
  event: string;
  data?: any;
  correlationId?: string;
  op?: string;
  component?: string;
  userId?: string;
  route?: string;
  duration_ms?: number;
  success?: boolean;
  err_code?: string;
  err_message?: string;
  sample?: boolean;
}

export class ClientLogger {
  private level: LogLevel;
  private enabled: boolean;

  constructor() {
    this.level = (import.meta.env?.VITE_LOG_LEVEL as LogLevel) || 'info';
    this.enabled = import.meta.env?.VITE_ENABLE_DIAGNOSTIC_LOGGING === 'true' ||
                   import.meta.env?.MODE === 'development';
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.enabled) {
      return false;
    }

    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };

    return levels[level] >= levels[this.level];
  }

  log(level: LogLevel, event: string, data?: any, ctx?: ClientLogContext): void {
    if (!this.shouldLog(level)) {
      return;
    }

    // Redact PII from data and context
    const redactedData = data ? redactPII(data) : undefined;
    const redactedCtx = ctx ? redactPII(ctx) : undefined;

    const entry: ClientLogEntry = {
      ts: new Date().toISOString(),
      level,
      event,
      data: redactedData,
      ...redactedCtx
    };

    const logMethod = level === 'error' ? console.error :
                     level === 'warn' ? console.warn :
                     level === 'info' ? console.info :
                     console.debug;

    logMethod('[SmartPrompt]', JSON.stringify(entry));
  }

  debug(event: string, data?: any, ctx?: ClientLogContext): void {
    this.log('debug', event, data, ctx);
  }

  info(event: string, data?: any, ctx?: ClientLogContext): void {
    this.log('info', event, data, ctx);
  }

  warn(event: string, data?: any, ctx?: ClientLogContext): void {
    this.log('warn', event, data, ctx);
  }

  error(event: string, data?: any, ctx?: ClientLogContext): void {
    this.log('error', event, data, ctx);
  }

  withCorrelation(correlationId?: string): ClientLogContext {
    return {
      correlationId: correlationId || crypto.randomUUID(),
      route: window.location.pathname
    };
  }

  timeIt<T>(op: string, fn: () => T | Promise<T>, ctx?: ClientLogContext): T | Promise<T> {
    const start = Date.now();
    const correlationId = ctx?.correlationId || crypto.randomUUID();

    this.debug(`${op}_start`, undefined, { ...ctx, correlationId, op });

    try {
      const result = fn();

      if (result instanceof Promise) {
        return result
          .then((value) => {
            const duration_ms = Date.now() - start;
            this.info(`${op}_end`, undefined, {
              ...ctx,
              correlationId,
              op,
              duration_ms,
              success: true
            });
            return value;
          })
          .catch((error) => {
            const duration_ms = Date.now() - start;
            this.error(`${op}_error`, undefined, {
              ...ctx,
              correlationId,
              op,
              duration_ms,
              success: false,
              err_code: error.code || error.name,
              err_message: error.message
            });
            throw error;
          });
      } else {
        const duration_ms = Date.now() - start;
        this.info(`${op}_end`, undefined, {
          ...ctx,
          correlationId,
          op,
          duration_ms,
          success: true
        });
        return result;
      }
    } catch (error: any) {
      const duration_ms = Date.now() - start;
      this.error(`${op}_error`, undefined, {
        ...ctx,
        correlationId,
        op,
        duration_ms,
        success: false,
        err_code: error.code || error.name,
        err_message: error.message
      });
      throw error;
    }
  }
}

export const logger = new ClientLogger();
export default logger;